import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import {
  getKbArticles,
  getKbArticle,
  createKbArticle,
  updateKbArticle,
  deleteKbArticle,
  getTicketProjects,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { PageHeading, Pill, NtIcon } from '../components/NotionUI';
import Select from '../components/Select';
import SearchBox from '../components/SearchBox';
import { canAction } from '../menu';
import { fmtDateDMY } from '../utils/format';

const emptyForm = { title: '', category: '', body: '', project_id: '', status: 'draft', is_public: false };

function Knowledge({ user, onLogout }) {
  const { t } = useLanguage();
  const canDelete = canAction(user, 'ticket_projects.manage');
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (status) params.status = status;
      const res = await getKbArticles(params);
      setArticles(res.articles || []);
      setCategories(res.categories || []);
    } catch (err) {
      setError(errorMessage(err, t('kb.loadError')));
    } finally {
      setLoading(false);
    }
  }, [search, status, t]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    getTicketProjects().then((d) => setProjects(d.projects || [])).catch(() => {});
  }, []);

  const startCreate = () => { setEditId(null); setForm(emptyForm); setShowForm(true); setError(''); };

  const startEdit = async (a) => {
    setError('');
    try {
      const { article } = await getKbArticle(a.id);
      setEditId(article.id);
      setForm({ title: article.title, category: article.category || '', body: article.body, project_id: article.project_id ? String(article.project_id) : '', status: article.status, is_public: article.is_public });
      setShowForm(true);
    } catch (err) {
      setError(errorMessage(err, t('kb.loadError')));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = { ...form, title: form.title.trim(), body: form.body.trim(), project_id: form.project_id ? Number(form.project_id) : null };
    try {
      if (editId) await updateKbArticle(editId, payload);
      else await createKbArticle(payload);
      setShowForm(false);
      await load();
    } catch (err) {
      setError(errorMessage(err, t('kb.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(t('kb.confirmDelete', { title: a.title }))) return;
    try {
      await deleteKbArticle(a.id);
      await load();
    } catch (err) {
      setError(errorMessage(err, t('kb.deleteError')));
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        <PageHeading icon="list" title={t('kb.title')} subtitle={t('kb.subtitle')} />
        {error && <div className="error-message">{error}</div>}

        {!showForm && (
          <>
            <div className="tp-toolbar">
              <SearchBox className="tk-search" value={search} onChange={setSearch} placeholder={t('kb.searchPlaceholder')} title={t('kb.searchHint')} />
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">{t('kb.anyStatus')}</option>
                <option value="published">{t('kb.status_published')}</option>
                <option value="draft">{t('kb.status_draft')}</option>
              </Select>
              <span className="tp-spacer" />
              <button className="btn" type="button" onClick={startCreate}>{t('kb.new')}</button>
            </div>
            {categories.length > 0 && <p className="nt-muted">{t('kb.categories')}: {categories.join(' · ')}</p>}
          </>
        )}

        {showForm && (
          <form className="tp-form" onSubmit={handleSubmit}>
            <section className="tp-section">
              <div className="tp-fields">
                <div className="form-group tp-full">
                  <label>{t('kb.fTitle')}</label>
                  <input value={form.title} maxLength={200} required onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('kb.fTitlePlaceholder')} />
                </div>
                <div className="form-group">
                  <label>{t('kb.fCategory')}</label>
                  <input list="kb-categories" value={form.category} maxLength={80} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                  <datalist id="kb-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
                </div>
                <div className="form-group">
                  <label>{t('kb.fQueue')}</label>
                  <Select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
                    <option value="">{t('kb.allQueues')}</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Select>
                </div>
                <div className="form-group tp-full">
                  <label>{t('kb.fBody')}</label>
                  <textarea rows={12} value={form.body} required onChange={(e) => setForm({ ...form, body: e.target.value })} />
                  <p className="tp-hint">{t('kb.bodyHint')}</p>
                </div>
                <div className="form-group">
                  <label>{t('kb.fStatus')}</label>
                  <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    <option value="draft">{t('kb.status_draft')}</option>
                    <option value="published">{t('kb.status_published')}</option>
                  </Select>
                </div>
                <div className="form-group">
                  <label className="rp-check" style={{ marginTop: 24 }}><input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} /> {t('kb.fPublic')}</label>
                  <p className="tp-hint">{t('kb.publicHint')}</p>
                </div>
              </div>
            </section>
            <div className="tp-savebar">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn" disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
            </div>
          </form>
        )}

        {!showForm && (loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : articles.length === 0 ? (
          <div className="au-empty">
            <span className="au-empty-icon" aria-hidden="true"><NtIcon name="list" size={26} /></span>
            <p>{search || status ? t('kb.noResults') : t('kb.empty')}</p>
            {!search && !status && <button className="btn" type="button" onClick={startCreate}>{t('kb.new')}</button>}
          </div>
        ) : (
          <div className="cn-list">
            {articles.map((a) => (
              <div key={a.id} className="cn-card">
                <div className="cn-head">
                  <h3 className="cn-title">{a.title}</h3>
                  {a.category && <Pill>{a.category}</Pill>}
                  <Pill dot color={a.status === 'published' ? '#2f9e6a' : '#8a8f98'}>{t(`kb.status_${a.status}`)}</Pill>
                  {a.is_public && <Pill color="#2383e2">{t('kb.public')}</Pill>}
                  <span className="tp-spacer" />
                  <button className="tp-icon" type="button" onClick={() => startEdit(a)} title={t('common.edit')} aria-label={t('common.edit')}><NtIcon name="edit" size={16} /></button>
                  {canDelete && <button className="tp-icon tp-icon-danger" type="button" onClick={() => handleDelete(a)} title={t('common.delete')} aria-label={t('common.delete')}><NtIcon name="trash" size={16} /></button>}
                </div>
                <p className="cn-body">{a.excerpt}</p>
                <p className="nt-muted rl-meta">
                  {t('kb.updated', { date: fmtDateDMY(a.updated_at) })}
                  {a.is_public && ` · ${t('kb.stats', { views: a.views, yes: a.helpful_yes, no: a.helpful_no })}`}
                </p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Layout>
  );
}

export default Knowledge;
